import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import api from "../../api";
import usePageMeta from "../../hooks/usePageMeta";
import useNavHighlight from "../../hooks/useNavHighlight";
import Loader from "../loader/loader";
import Pagination from "../shared/Pagination";
import StoreSelector from "./StoreSelector";
import normalizeUpc from "../../utils/normalizeUpc";
import { parseExcelDatetime } from "../../utils/parseExcelDatetime";
import {
    toDatetimeLocalValue,
    datetimeLocalToIso,
    browserDatetimeIso,
} from "../../utils/datetimeHelpers";

const RECORD_FIELDS_PURCHASE = ["upc", "quantity", "recorded_at", "unit_cost"];
const RECORD_FIELDS_SALE = ["upc", "quantity", "recorded_at", "unit_retail"];
const REQUIRED_FIELDS = ["upc", "quantity"];

const StockRecordPage = ({
    type,
    title,
    navId,
    metaKey,
    apiBase,
    breadcrumbParent = "Dashboard",
}) => {
    const { user: currentUser } = useSelector((state) => state.verifyEmailStore);
    const isPurchase = type === "purchase";
    const recordFields = isPurchase ? RECORD_FIELDS_PURCHASE : RECORD_FIELDS_SALE;

    const now = new Date();
    const [tab, setTab] = useState("list");
    const [storeId, setStoreId] = useState("");
    const [page, setPage] = useState(1);
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [upcFilter, setUpcFilter] = useState("");
    const [records, setRecords] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [formUpc, setFormUpc] = useState("");
    const [formQty, setFormQty] = useState("1");
    const [formDatetime, setFormDatetime] = useState(toDatetimeLocalValue());
    const [formUnitCost, setFormUnitCost] = useState("");
    const [formUnitRetail, setFormUnitRetail] = useState("");

    const [rawRows, setRawRows] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [previewRows, setPreviewRows] = useState([]);
    const [mapping, setMapping] = useState({});
    const [hasHeader, setHasHeader] = useState(true);
    const [importing, setImporting] = useState(false);

    useNavHighlight(navId);
    usePageMeta(metaKey);

    useEffect(() => {
        if (currentUser?.store_id) {
            setStoreId(String(currentUser.store_id));
        }
    }, [currentUser?.store_id]);

    const storeBody = currentUser?.role === "Admin" && storeId ? { store_id: Number(storeId) } : {};

    const fetchRecords = useCallback(async () => {
        if (currentUser?.role === "Admin" && !storeId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                year: String(year),
                month: String(month),
            });
            if (upcFilter.trim()) params.set("upc", upcFilter.trim());
            if (currentUser?.role === "Admin" && storeId) params.set("store_id", storeId);

            const { data } = await api.get(`${apiBase}?${params.toString()}`);
            if (data.success) {
                setRecords(data.records || []);
                setTotalPages(data.totalPages || 1);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || `Failed to load ${title.toLowerCase()}`);
        } finally {
            setLoading(false);
        }
    }, [apiBase, page, year, month, upcFilter, storeId, currentUser?.role, title]);

    useEffect(() => {
        if (tab === "list") fetchRecords();
    }, [tab, fetchRecords]);

    const handleSaveManual = async (e) => {
        e.preventDefault();
        if (currentUser?.role === "Admin" && !storeId) {
            toast.error("Select a store");
            return;
        }

        const normalized = normalizeUpc(formUpc);
        if (!normalized) {
            toast.error("Invalid UPC");
            return;
        }

        const qty = Number(formQty);
        if (!Number.isInteger(qty) || qty < 1) {
            toast.error("Quantity must be a positive integer");
            return;
        }

        const recorded_at = datetimeLocalToIso(formDatetime);
        if (!recorded_at) {
            toast.error("Invalid date/time");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                upc: normalized,
                quantity: qty,
                recorded_at,
                ...storeBody,
            };
            if (isPurchase) {
                if (formUnitCost !== "") payload.unit_cost = Number(formUnitCost);
            } else if (formUnitRetail !== "") {
                payload.unit_retail = Number(formUnitRetail);
            }

            await api.post(apiBase, payload);
            toast.success("Record saved");
            setFormUpc("");
            setFormQty("1");
            setFormDatetime(toDatetimeLocalValue());
            setFormUnitCost("");
            setFormUnitRetail("");
            setTab("list");
            fetchRecords();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save record");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this record?")) return;
        try {
            const url = currentUser?.role === "Admin" && storeId
                ? `${apiBase}/${id}?store_id=${storeId}`
                : `${apiBase}/${id}`;
            await api.delete(url);
            toast.success("Record deleted");
            fetchRecords();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to delete");
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const workbook = XLSX.read(evt.target.result, { type: "binary" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            setRawRows(data);
            setMapping({});
        };
        reader.readAsBinaryString(file);
    };

    useEffect(() => {
        if (!rawRows.length) return;
        if (hasHeader) {
            setHeaders(rawRows[0]);
            setPreviewRows(rawRows.slice(1, 11));
        } else {
            setHeaders(rawRows[0].map((_, i) => `Column ${i + 1}`));
            setPreviewRows(rawRows.slice(0, 10));
        }
    }, [rawRows, hasHeader]);

    const buildImportRecords = () => {
        const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
        const defaultIso = browserDatetimeIso();
        const built = [];

        const optionalMoney = (value) => {
            if (value === undefined || value === null || value === "") return undefined;
            const n = Number(value);
            if (!Number.isFinite(n) || n <= 0) return undefined;
            return n;
        };

        dataRows.forEach((row) => {
            const record = {};
            Object.entries(mapping).forEach(([colIndex, field]) => {
                if (!field) return;
                record[field] = row[Number(colIndex)];
            });

            if (!record.upc) return;

            const normalized = normalizeUpc(record.upc);
            if (!normalized) return;

            let recorded_at = null;
            if (record.recorded_at !== undefined && record.recorded_at !== "") {
                const parsed = parseExcelDatetime(record.recorded_at);
                recorded_at = parsed ? parsed.toISOString() : defaultIso;
            } else {
                recorded_at = defaultIso;
            }

            const unitCost = optionalMoney(record.unit_cost);
            const unitRetail = optionalMoney(record.unit_retail);

            built.push({
                upc: normalized,
                quantity: Number(record.quantity) || 1,
                recorded_at,
                ...(isPurchase && unitCost !== undefined ? { unit_cost: unitCost } : {}),
                ...(!isPurchase && unitRetail !== undefined ? { unit_retail: unitRetail } : {}),
            });
        });

        return built;
    };

    const handleImport = async () => {
        if (currentUser?.role === "Admin" && !storeId) {
            toast.error("Select a store");
            return;
        }

        const recordsToImport = buildImportRecords();
        if (!recordsToImport.length) {
            toast.error("No valid rows to import. Map UPC and Quantity columns.");
            return;
        }

        setImporting(true);
        try {
            const { data } = await api.post(`${apiBase}/import/bulk`, {
                records: recordsToImport,
                default_recorded_at: browserDatetimeIso(),
                ...storeBody,
            });
            if (data.success) {
                toast.success(`Import done: ${data.inserted} added, ${data.updated} updated, ${data.skipped} skipped`);
                setRawRows([]);
                setMapping({});
                setTab("list");
                fetchRecords();
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
    ];

    return (
        <Fragment>
            {(loading || saving || importing) && <Loader />}

            <section className="pl-[1.5vmax] pr-[2.5vmax]">
                <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax]">
                    <h2 className="text-[1.3vmax] font-bold">{title}</h2>
                    <div className="flex-center gap-[0.8vmax] text-[1vmax]">
                        <Link to={breadcrumbParent === "Admin" ? "/admin" : "/"}>{breadcrumbParent}</Link>
                        <div className="w-[0.3vmax] h-[0.3vmax] rounded-[50%] bg-black" />
                        <span className="text-slate-400">{title}</span>
                    </div>
                </div>
            </section>

            <div className="px-[2.5vmax] mt-4 flex flex-wrap items-center justify-between gap-4">
                <StoreSelector storeId={storeId} setStoreId={setStoreId} user={currentUser} />
                <div className="flex gap-2">
                    {["list", "add", "import"].map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setTab(t)}
                            className={`px-4 py-2 rounded-lg text-[13px] font-semibold capitalize ${
                                tab === t
                                    ? "bg-[var(--color-2)] text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                            {t === "list" ? "Records" : t === "add" ? "Add Manual" : "Import Excel"}
                        </button>
                    ))}
                </div>
            </div>

            {tab === "list" && (
                <section className="px-[2.5vmax] mt-4">
                    <div className="bg-white rounded-2xl shadow-1 p-4 mb-4 flex flex-wrap gap-3 items-end">
                        <div>
                            <label className="text-[12px] font-semibold text-gray-500 block mb-1">Month</label>
                            <select
                                value={month}
                                onChange={(e) => { setMonth(Number(e.target.value)); setPage(1); }}
                                className="border rounded-lg px-3 py-2 text-[13px]"
                            >
                                {months.map((m, i) => (
                                    <option key={m} value={i + 1}>{m}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[12px] font-semibold text-gray-500 block mb-1">Year</label>
                            <input
                                type="number"
                                value={year}
                                onChange={(e) => { setYear(Number(e.target.value)); setPage(1); }}
                                className="border rounded-lg px-3 py-2 text-[13px] w-24"
                            />
                        </div>
                        <div>
                            <label className="text-[12px] font-semibold text-gray-500 block mb-1">UPC filter</label>
                            <input
                                type="text"
                                value={upcFilter}
                                onChange={(e) => setUpcFilter(e.target.value)}
                                placeholder="Search UPC"
                                className="border rounded-lg px-3 py-2 text-[13px]"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => { setPage(1); fetchRecords(); }}
                            className="px-4 py-2 bg-[var(--color-2)] text-white rounded-lg text-[13px] font-semibold"
                        >
                            Apply
                        </button>
                    </div>

                    <div className="bg-white rounded-2xl shadow-1 overflow-hidden">
                        <div className="grid grid-cols-[1.2fr_70px_90px_160px_80px_70px] gap-2 bg-slate-100 p-3 text-[12px] font-bold text-slate-600">
                            <span className="th-text">UPC</span>
                            <span className="th-center">Qty</span>
                            <span className="th-num">{isPurchase ? "Each Cost" : "Each Retail"}</span>
                            <span className="th-center">Date / Time</span>
                            <span className="th-center">Source</span>
                            <span className="th-center">Action</span>
                        </div>
                        {records.length === 0 ? (
                            <p className="text-center text-gray-400 py-10 text-[14px]">No records for this period</p>
                        ) : records.map((r) => {
                            const money = isPurchase
                                ? Number(r.unit_cost || 0)
                                : Number(r.unit_retail || 0);
                            return (
                            <div
                                key={r.id}
                                className="grid grid-cols-[1.2fr_70px_90px_160px_80px_70px] gap-2 p-3 border-t border-gray-100 text-[13px] items-center"
                            >
                                <span className="font-mono td-num">{r.upc}</span>
                                <span className="td-center font-bold">{r.quantity}</span>
                                <span className="td-num text-slate-600">
                                    {money > 0 ? `$${money.toFixed(2)}` : "—"}
                                </span>
                                <span className="td-text">{new Date(r.recorded_at).toLocaleString()}</span>
                                <span className="td-center capitalize text-gray-500">{r.source}</span>
                                <span className="td-center">
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(r.id)}
                                        className="text-red-500 hover:text-red-700"
                                        title="Delete"
                                    >
                                        <i className="fa-solid fa-trash" />
                                    </button>
                                </span>
                            </div>
                            );
                        })}
                    </div>
                    <Pagination page={page} totalPages={totalPages} setPage={setPage} />
                </section>
            )}

            {tab === "add" && (
                <section className="px-[2.5vmax] mt-4 max-w-lg">
                    <form onSubmit={handleSaveManual} className="bg-white rounded-2xl shadow-1 p-6 space-y-4">
                        <div>
                            <label className="text-[13px] font-semibold text-gray-700 block mb-1">UPC</label>
                            <input
                                type="text"
                                value={formUpc}
                                onChange={(e) => setFormUpc(e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 font-mono"
                                placeholder="Scan or enter UPC"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-[13px] font-semibold text-gray-700 block mb-1">Quantity</label>
                            <input
                                type="number"
                                min="1"
                                value={formQty}
                                onChange={(e) => setFormQty(e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                                required
                            />
                        </div>
                        {isPurchase ? (
                            <div>
                                <label className="text-[13px] font-semibold text-gray-700 block mb-1">
                                    Each Cost <span className="text-slate-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formUnitCost}
                                    onChange={(e) => setFormUnitCost(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="Blank = use Store Price Book in Inventory Lookup"
                                />
                            </div>
                        ) : (
                            <div>
                                <label className="text-[13px] font-semibold text-gray-700 block mb-1">
                                    Each Retail{" "}
                                    <span className="text-slate-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formUnitRetail}
                                    onChange={(e) => setFormUnitRetail(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="Blank = use Store Price Book in Inventory Lookup"
                                />
                            </div>
                        )}
                        <div>
                            <label className="text-[13px] font-semibold text-gray-700 block mb-1">Date & Time</label>
                            <input
                                type="datetime-local"
                                value={formDatetime}
                                onChange={(e) => setFormDatetime(e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={saving}
                            className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold disabled:opacity-50"
                        >
                            {saving ? "Saving..." : "Save Record"}
                        </button>
                    </form>
                </section>
            )}

            {tab === "import" && (
                <section className="px-[2.5vmax] mt-4">
                    <div className="bg-white rounded-2xl shadow-1 p-6">
                        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} className="mb-4" />
                        <label className="flex items-center gap-2 text-[13px] mb-4">
                            <input
                                type="checkbox"
                                checked={hasHeader}
                                onChange={(e) => setHasHeader(e.target.checked)}
                            />
                            First row is header
                        </label>

                        {headers.length > 0 && (
                            <>
                                <p className="text-[13px] font-semibold mb-2">Map columns</p>
                                <p className="text-[12px] text-slate-500 mb-3">
                                    Required: <strong>upc</strong>, <strong>quantity</strong>.
                                    Optional: <strong>recorded_at</strong>
                                    {isPurchase ? (
                                        <>, <strong>unit_cost</strong> (blank → Store Price Book in Inventory Lookup).</>
                                    ) : (
                                        <>, <strong>unit_retail</strong> (blank → Store Price Book in Inventory Lookup).</>
                                    )}
                                </p>
                                <div className="flex flex-wrap gap-3 mb-4">
                                    {headers.map((h, i) => (
                                        <div key={i} className="flex flex-col gap-1">
                                            <span className="text-[11px] text-gray-500 truncate max-w-[120px]">{h || `Col ${i + 1}`}</span>
                                            <select
                                                value={mapping[i] || ""}
                                                onChange={(e) => setMapping((m) => ({ ...m, [i]: e.target.value }))}
                                                className="border rounded px-2 py-1 text-[12px]"
                                            >
                                                <option value="">— skip —</option>
                                                {recordFields.map((f) => (
                                                    <option key={f} value={f}>
                                                        {f}{REQUIRED_FIELDS.includes(f) ? " *" : ""}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>

                                {previewRows.length > 0 && (
                                    <div className="overflow-x-auto mb-4 text-[12px]">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr>
                                                    {headers.map((h, i) => (
                                                        <th key={i} className="border p-1 bg-gray-50">{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewRows.map((row, ri) => (
                                                    <tr key={ri}>
                                                        {row.map((cell, ci) => (
                                                            <td key={ci} className="border p-1">{String(cell ?? "")}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={handleImport}
                                    disabled={importing}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold disabled:opacity-50"
                                >
                                    {importing ? "Importing..." : "Import Records"}
                                </button>
                            </>
                        )}
                    </div>
                </section>
            )}
        </Fragment>
    );
};

export default StockRecordPage;
